import { css, styled, Switch as MuiSwitch, switchClasses as muiSwitchClasses } from '@mui/material';

import { switchClasses } from './theme';

import type { Theme } from '../../types';
import type { SwitchOwnerState, SwitchProps } from './types';

const Switch = (props: SwitchProps) => {
  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 컴포넌트에서 직접 처리
  const {
    color = 'default', // theme defaultProps에서 이동
    size = 'sm', // theme defaultProps에서 이동
    text,
    description,
    className,
    ref,
    inputRef,
    style,
    ...others
  } = props;

  const ownerState: SwitchOwnerState = {
    ...props,
    color,
    size,
  };

  return (
    <SwitchRoot className={className} ref={ref} style={style} ownerState={ownerState}>
      <SwitchSwitch
        checkedIcon={<SwitchSwitchHandle ownerState={ownerState} />}
        disableFocusRipple
        disableRipple
        disableTouchRipple
        icon={<SwitchSwitchHandle ownerState={ownerState} />}
        inputRef={inputRef}
        size={size}
        ownerState={ownerState}
        {...others}
      />
      {(text || description) && (
        <SwitchTextContainer ownerState={ownerState}>
          {text && <SwitchStateText ownerState={ownerState}>{text}</SwitchStateText>}
          {description && (
            <SwitchDescription ownerState={ownerState}>{description}</SwitchDescription>
          )}
        </SwitchTextContainer>
      )}
    </SwitchRoot>
  );
};

Switch.displayName = 'Switch';

// Styled Components - 하이브리드 패턴
const SwitchRoot = styled('span', {
  target: switchClasses.root,
})<{ ownerState: SwitchOwnerState }>`
  display: inline-flex;
  gap: 8px;
`;

const SwitchSwitch = styled(MuiSwitch, {
  target: switchClasses.switch,
})<{ ownerState: SwitchOwnerState }>(({ ownerState, theme }) => {
  const sizeStyles = getSwitchSizeStyles(ownerState.size);

  return css`
    border-radius: 999px;
    overflow: initial;
    ${theme.mixins.focusRing('outside', {
      selectors: { focus: `&:has(.${muiSwitchClasses.switchBase}.Mui-focusVisible)` },
      borderRadius: 999,
      inverse: ownerState.color === 'global',
    })};

    & .${muiSwitchClasses.switchBase} {
      &:is(:hover, .${muiSwitchClasses.checked}, .${muiSwitchClasses.checked}:hover) {
        background-color: ${theme.color['base/transparent']};
      }
    }

    & .${muiSwitchClasses.track} {
      opacity: initial;
      border-radius: 999px;
      background-color: ${switchColor({ theme, ownerState }).track.idle};
    }

    &:hover .${muiSwitchClasses.track} {
      background-color: ${switchColor({ theme, ownerState }).track.hover};
    }

    & .${muiSwitchClasses.switchBase}.${muiSwitchClasses.checked} + .${muiSwitchClasses.track} {
      opacity: initial;
      background-color: ${switchColor({ theme, ownerState }).track.idle};
    }

    &:hover
      .${muiSwitchClasses.switchBase}.${muiSwitchClasses.checked}:not(.${muiSwitchClasses.disabled})
      + .${muiSwitchClasses.track} {
      background-color: ${switchColor({ theme, ownerState }).track.hover};
    }

    & .${muiSwitchClasses.switchBase}.${muiSwitchClasses.disabled} + .${muiSwitchClasses.track} {
      opacity: initial;
      background-color: ${switchColor({ theme, ownerState }).track.disabled};
    }

    ${sizeStyles}
  `;
});

const SwitchSwitchHandle = styled('span', {
  target: switchClasses.switchHandle,
})<{ ownerState: SwitchOwnerState }>(({ ownerState, theme }) => {
  const handleSizeStyles = getHandleSizeStyles(ownerState.size);

  return css`
    display: inline-block;
    border-radius: 999px;
    background-color: ${switchColor({ theme, ownerState }).handle.idle};
    box-shadow: ${theme.elevation.level_1};
    transition: box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    .${switchClasses.switch}:hover & {
      box-shadow: ${theme.elevation.level_2};
    }

    .${muiSwitchClasses.switchBase}.${muiSwitchClasses.checked} & {
      box-shadow: none;
    }

    .${switchClasses.switch}:is(span, :hover)
      .${muiSwitchClasses.switchBase}.${muiSwitchClasses.disabled}
      & {
      box-shadow: none;
      background-color: ${switchColor({ theme, ownerState }).handle.disabled};
    }

    ${handleSizeStyles}
  `;
});

const SwitchTextContainer = styled('div', {
  target: switchClasses.textContainer,
})<{ ownerState: SwitchOwnerState }>(({ ownerState }) => {
  const textContainerPaddingStyles = getTextContainerPaddingStyles(ownerState.size);

  return css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    ${textContainerPaddingStyles}
  `;
});

const SwitchStateText = styled('span', {
  target: switchClasses.stateText,
})<{ ownerState: SwitchOwnerState }>(({ ownerState, theme }) => {
  const colorStyles = getStateTextColorStyles(ownerState.color, theme);

  return css`
    ${theme.typography['label/regular/md']};
    display: block;
    ${colorStyles}
  `;
});

const SwitchDescription = styled('span', {
  target: switchClasses.description,
})<{ ownerState: SwitchOwnerState }>(({ ownerState, theme }) => {
  const colorStyles = getDescriptionColorStyles(ownerState.color, theme);

  return css`
    ${theme.typography['label/regular/xs']};
    display: block;
    ${colorStyles}
  `;
});

// 스타일 헬퍼 함수들
const getSwitchSizeStyles = (size: SwitchOwnerState['size']) => {
  const sizeStylesMap = {
    sm: css`
      width: 28px;
      height: 16px;
      margin: 6px 0px;
      padding: 0px;

      & .${muiSwitchClasses.switchBase} {
        padding: 2px 2px;

        &.${muiSwitchClasses.checked} {
          transform: translateX(12px);
        }
      }

      & .${muiSwitchClasses.switchBase} .${muiSwitchClasses.input} {
        left: 0;
        width: 28px;
      }

      & .${muiSwitchClasses.switchBase}.${muiSwitchClasses.checked} .${muiSwitchClasses.input} {
        left: -12px;
      }
    `,
    md: css`
      width: 34px;
      height: 20px;
      margin: 6px 0px;
      padding: 0px;

      & .${muiSwitchClasses.switchBase} {
        padding: 2px 2px;

        &.${muiSwitchClasses.checked} {
          transform: translateX(14px);
        }
      }

      & .${muiSwitchClasses.switchBase} .${muiSwitchClasses.input} {
        left: 0;
        width: 34px;
      }

      & .${muiSwitchClasses.switchBase}.${muiSwitchClasses.checked} .${muiSwitchClasses.input} {
        left: -14px;
      }
    `,
    lg: css`
      width: 44px;
      height: 24px;
      margin: 8px 0px;
      padding: 0px;

      & .${muiSwitchClasses.switchBase} {
        padding: 2px 2px;

        &.${muiSwitchClasses.checked} {
          transform: translateX(20px);
        }
      }

      & .${muiSwitchClasses.switchBase} .${muiSwitchClasses.input} {
        left: 0;
        width: 44px;
      }

      & .${muiSwitchClasses.switchBase}.${muiSwitchClasses.checked} .${muiSwitchClasses.input} {
        left: -20px;
      }
    `,
  };

  return sizeStylesMap[size ?? 'sm'] || css``;
};

const getHandleSizeStyles = (size: SwitchOwnerState['size']) => {
  const handleSizeStylesMap = {
    sm: css`
      width: 12px;
      height: 12px;
    `,
    md: css`
      width: 16px;
      height: 16px;
    `,
    lg: css`
      width: 20px;
      height: 20px;
    `,
  };

  return handleSizeStylesMap[size ?? 'sm'] || css``;
};

const getTextContainerPaddingStyles = (size: SwitchOwnerState['size']) => {
  const paddingStylesMap = {
    sm: css`
      padding: 3.5px 0px;
    `,
    md: css`
      padding: 5.5px 0px;
    `,
    lg: css`
      padding: 9.5px 0px;
    `,
  };

  return paddingStylesMap[size ?? 'sm'] || css``;
};

const getStateTextColorStyles = (color: SwitchOwnerState['color'], theme: Theme) => {
  const colorStylesMap = {
    default: css`
      color: ${theme.color['text/weak/default']};
    `,
    global: css`
      color: ${theme.color['text/inverse/weak/default']};
    `,
  };

  return colorStylesMap[color ?? 'default'] || css``;
};

const getDescriptionColorStyles = (color: SwitchOwnerState['color'], theme: Theme) => {
  const colorStylesMap = {
    default: css`
      color: ${theme.color['text/weaker/default']};
    `,
    global: css`
      color: ${theme.color['text/inverse/weaker/default']};
    `,
  };

  return colorStylesMap[color ?? 'default'] || css``;
};

// switchColor 헬퍼 함수 (theme.ts에서 이동)
const switchColor = (props: { ownerState: SwitchOwnerState; theme: Theme }) => {
  const { ownerState, theme } = props;
  const mapper: Record<
    'default' | 'global',
    Record<
      'unchecked' | 'checked',
      Record<'track' | 'handle', Record<'idle' | 'hover' | 'disabled', string>>
    >
  > = {
    default: {
      unchecked: {
        track: {
          idle: theme.color['interactive/weak/default'],
          hover: theme.color['interactive/weak/hover'],
          disabled: theme.color['interactive/disabled'],
        },
        handle: {
          idle: theme.color['interactive/white/default'],
          hover: theme.color['interactive/white/default'],
          disabled: theme.color['interactive/white/disabled'],
        },
      },
      checked: {
        track: {
          idle: theme.color['interactive/strongest/default'],
          hover: theme.color['interactive/strongest/hover'],
          disabled: theme.color['interactive/disabled'],
        },
        handle: {
          idle: theme.color['interactive/white/default'],
          hover: theme.color['interactive/white/default'],
          disabled: theme.color['interactive/white/disabled'],
        },
      },
    },
    global: {
      unchecked: {
        track: {
          idle: theme.color['interactive/global/strong/default'],
          hover: theme.color['interactive/global/strong/hover'],
          disabled: theme.color['interactive/global/strong/disabled'],
        },
        handle: {
          idle: theme.color['interactive/global/weakest/default'],
          hover: theme.color['interactive/global/weakest/default'],
          disabled:
            '#323841' /* FIXME (@walter-24) theme.color['interactive/global/weaker/disabled'] */,
        },
      },
      checked: {
        track: {
          idle: '#F6F7F9' /* FIXME (@walter-24) theme.color['interactive/global/strongest/default'] 컬러값이 피그마와 다름 */,
          hover: theme.color['interactive/global/strongest/hover'],
          disabled: theme.color['interactive/global/weakest/disabled'],
        },
        handle: {
          idle: theme.color['interactive/global/weakest/default'],
          hover: theme.color['interactive/global/weakest/default'],
          disabled:
            '#323841' /* FIXME (@walter-24) theme.color['interactive/global/weaker/disabled'] */,
        },
      },
    },
  };
  return mapper[ownerState.color ?? 'default'][ownerState.checked ? 'checked' : 'unchecked'];
};

export default Switch;
